import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { track } from "../../lib/analytics";
import { Copy, Link as LinkIcon, RefreshCw, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { copyToClipboard } from "../../lib/share";
import { currentIntlLocale } from "../../lib/i18n";

type Invite = {
  id: string;
  token: string;
  is_active: boolean;
  uses: number;
  created_at: string;
  expires_at: string | null;
  max_uses: number | null;
};

// 24 char base62: ~142 bit di entropia, sufficiente per non essere indovinato.
function generateToken(): string {
  const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  for (let i = 0; i < arr.length; i++) s += ALPHABET[arr[i] % ALPHABET.length];
  return s;
}

// Opzioni di durata: 7d / 30d (default consigliato) / 90d. Vista la
// natura "share via WhatsApp" del link, 30 giorni e un buon compromesso
// tra praticita e rischio leak.
const EXPIRY_DAYS_OPTIONS = [7, 30, 90] as const;

export function TeamInviteLinkCard({ teamId, teamName }: { teamId: string; teamName: string }) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiryDays, setExpiryDays] = useState<number>(30);
  const [maxUses, setMaxUses] = useState<string>("");

  const invitesQuery = useQuery({
    queryKey: ["team-invites", teamId],
    queryFn: async () => {
      const { data, error: err } = await supabaseV2
        .from("team_invites")
        .select("id, token, is_active, uses, created_at, expires_at, max_uses")
        .eq("team_id", teamId)
        .order("created_at", { ascending: false })
        .limit(1);
      if (err) throw err;
      return (data || []) as Invite[];
    },
  });

  const current = (invitesQuery.data || [])[0] || null;
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://app.elevenbase.pro";
  const fullUrl = current ? `${baseUrl}/join/${current.token}` : "";

  const isExpired = current?.expires_at ? new Date(current.expires_at) < new Date() : false;
  const isExhausted = current?.max_uses != null && current.uses >= current.max_uses;

  const buildInsertPayload = (days: number, max: string) => {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + days);
    const maxN = max.trim() ? Math.max(1, parseInt(max, 10)) : null;
    return {
      team_id: teamId,
      token: generateToken(),
      is_active: true,
      expires_at: expiresAt.toISOString(),
      max_uses: maxN,
    };
  };

  const create = useMutation({
    mutationFn: async () => {
      const payload = buildInsertPayload(expiryDays, maxUses);
      const { error: err } = await supabaseV2.from("team_invites").insert(payload);
      if (err) throw err;
      // expires_in_days e max_uses servono come param GA4 nell'evento sottostante.
      return { expires_in_days: expiryDays, max_uses: payload.max_uses };
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["team-invites", teamId] });
      track.inviteLink("created", {
        team_id: teamId,
        expires_in_days: res?.expires_in_days,
        max_uses: res?.max_uses,
      });
    },
    onError: (e: any) => setError(e.message || String(e)),
  });

  const regenerate = useMutation({
    mutationFn: async () => {
      if (!current) return null;
      // Rigenerazione: nuovo token, reset uses, nuova scadenza dalla scelta corrente.
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiryDays);
      const maxN = maxUses.trim() ? Math.max(1, parseInt(maxUses, 10)) : null;
      const { error: err } = await supabaseV2
        .from("team_invites")
        .update({
          token: generateToken(),
          is_active: true,
          uses: 0,
          expires_at: expiresAt.toISOString(),
          max_uses: maxN,
        })
        .eq("id", current.id);
      if (err) throw err;
      return { expires_in_days: expiryDays, max_uses: maxN };
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["team-invites", teamId] });
      track.inviteLink("regenerated", {
        team_id: teamId,
        expires_in_days: res?.expires_in_days,
        max_uses: res?.max_uses,
      });
    },
    onError: (e: any) => setError(e.message || String(e)),
  });

  const toggle = useMutation({
    mutationFn: async () => {
      if (!current) return null;
      const willBeActive = !current.is_active;
      const { error: err } = await supabaseV2
        .from("team_invites")
        .update({ is_active: willBeActive })
        .eq("id", current.id);
      if (err) throw err;
      return { willBeActive };
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["team-invites", teamId] });
      if (res) {
        track.inviteLink(res.willBeActive ? "activated" : "deactivated", { team_id: teamId });
      }
    },
    onError: (e: any) => setError(e.message || String(e)),
  });

  const needsAttention = !invitesQuery.isLoading && !current;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(currentIntlLocale(), {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <Card
      className={
        "transition-shadow" +
        (needsAttention ? "" : "")
      }
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <LinkIcon className="h-4 w-4 text-primary" />
          {t("team.invite.title")}
        </CardTitle>
        <CardDescription>
          <Trans
            i18nKey="team.invite.description"
            t={t}
            values={{ teamName }}
            components={[<strong />]}
          />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {error}
          </div>
        )}

        {/* Picker scadenza + max_uses: visibile sia per CREATE sia per REGENERATE. */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-semibold">
              {t("team.invite.expiry", { defaultValue: "Scadenza" })}
            </label>
            <Select value={String(expiryDays)} onValueChange={(v) => setExpiryDays(Number(v))}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_DAYS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {t("team.invite.expiryDays", { count: d, defaultValue: "{{count}} giorni" })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-semibold">
              {t("team.invite.maxUses", { defaultValue: "Usi massimi (opzionale)" })}
            </label>
            <Input
              type="number"
              min="1"
              placeholder={t("team.invite.maxUsesUnlimited", { defaultValue: "Illimitato" })}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
        </div>

        {invitesQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">{t("team.invite.loading")}</p>
        ) : !current ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t("team.invite.empty")}</p>
            <Button size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
              <LinkIcon />
              {create.isPending ? t("team.invite.creating") : t("team.invite.create")}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-1.5">
              <Input value={fullUrl} readOnly className="h-9 text-sm font-mono flex-1 min-w-0" />
              <Button
                size="sm"
                variant="outline"
                className="h-9"
                onClick={async () => {
                  const ok = await copyToClipboard(fullUrl);
                  if (ok) {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                    track.inviteLink("copied", { team_id: teamId });
                  }
                }}
              >
                <Copy />
                {copied ? t("team.invite.copied") : t("team.invite.copy")}
              </Button>
            </div>

            {/* Banner stato: scaduto / esaurito / attivo. */}
            {isExpired ? (
              <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {t("team.invite.expiredBanner", {
                  date: current.expires_at ? formatDate(current.expires_at) : "",
                  defaultValue: "Link scaduto il {{date}}. Rigeneralo per attivare un nuovo invito.",
                })}
              </div>
            ) : isExhausted ? (
              <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700">
                {t("team.invite.exhaustedBanner", {
                  uses: current.uses,
                  max: current.max_uses,
                  defaultValue: "Link esaurito ({{uses}}/{{max}} usi). Rigeneralo per riaprire l'invito.",
                })}
              </div>
            ) : null}

            <p className="text-xs text-muted-foreground">
              {current.uses === 1
                ? t("team.invite.usesOne", {
                    count: current.uses,
                    state: current.is_active ? t("team.invite.linkActive") : t("team.invite.linkInactive"),
                  })
                : t("team.invite.usesMany", {
                    count: current.uses,
                    state: current.is_active ? t("team.invite.linkActive") : t("team.invite.linkInactive"),
                  })}
              {current.max_uses != null && (
                <>
                  {" · "}
                  {t("team.invite.maxUsesInfo", {
                    max: current.max_uses,
                    defaultValue: "max {{max}}",
                  })}
                </>
              )}
              {current.expires_at && (
                <>
                  {" · "}
                  {t("team.invite.expiresOn", {
                    date: formatDate(current.expires_at),
                    defaultValue: "scade il {{date}}",
                  })}
                </>
              )}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (window.confirm(t("team.invite.regenerateConfirm"))) {
                    regenerate.mutate();
                  }
                }}
                disabled={regenerate.isPending}
              >
                <RefreshCw />
                {t("team.invite.regenerate")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggle.mutate()} disabled={toggle.isPending}>
                {current.is_active ? <ToggleRight /> : <ToggleLeft />}
                {current.is_active ? t("team.invite.deactivate") : t("team.invite.reactivate")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
